import cn from 'classnames';
import type { FC, PropsWithChildren, ReactNode } from 'react';
import type { IconType } from 'react-icons';
import { RiAlertFill, RiCheckboxCircleFill, RiInformationFill } from 'react-icons/ri';

import { _notNil } from '@/littledash';

import styles from './InlineBanner.module.scss';

export type InlineBannerVariant = 'success' | 'warning' | 'critical' | 'info';

interface InlineBannerProps {
  variant?: InlineBannerVariant;
  title?: ReactNode;
  noIcon?: boolean;
  className?: string;
  testId?: string;
}

const variantIcons: Record<InlineBannerVariant, IconType> = {
  success: RiCheckboxCircleFill,
  warning: RiAlertFill,
  critical: RiAlertFill,
  info: RiInformationFill,
};

/**
 * Status banner with a coloured left accent bar, used to report the outcome of an action
 * next to the content it relates to. Unlike `Banner`, it is neither dismissible nor raised,
 * so it sits flat inside modals, panels and forms.
 */
const InlineBanner: FC<PropsWithChildren<InlineBannerProps>> = ({
  variant = 'info',
  title,
  noIcon = false,
  className,
  children,
  testId = 'inline-banner',
}) => {
  const VariantIcon = variantIcons[variant];

  return (
    <div
      className={cn(styles.banner, styles[variant], className)}
      role={variant === 'critical' ? 'alert' : 'status'}
      data-test-component="InlineBanner"
      data-test-element="container"
      data-testid={testId}
    >
      {!noIcon && <VariantIcon className={styles.icon} size={20} aria-hidden data-testid={`${testId}__icon`} />}
      <div className={styles.content} data-test-element="content">
        {_notNil(title) && (
          <div className={styles.title} data-test-element="title">
            {title}
          </div>
        )}
        {children}
      </div>
    </div>
  );
};

export default InlineBanner;
